/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef, useContext, memo } from 'react'
import {
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  Input,
  Heading,
  UnorderedList,
  ListItem
} from '@chakra-ui/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import { activities, universalRoomOptions } from '../../activities'
import SideNav from '../../components/SideNav'
import { Nostalgist } from 'nostalgist'
import CryptoJS, { MD5 } from 'crypto-js'

const buttonMappingByPlayers: Record<
  number,
  Record<number, { button: string; player: number }>
> = {
  1: {
    1: { button: 'left', player: 1 }
  },
  2: {
    1: { button: 'left', player: 1 },
    2: { button: 'left', player: 2 }
  },
  3: {
    1: { button: 'left', player: 1 },
    2: { button: 'a', player: 1 },
    3: { button: 'left', player: 2 }
  },
  4: {
    1: { button: 'left', player: 1 },
    2: { button: 'a', player: 1 },
    3: { button: 'left', player: 2 },
    4: { button: 'a', player: 2 }
  }
}

let CHANNEL_GLOBAL: any = null

interface PlayerType {
  clientId: string
}
let isConnecting = false
// const CHANNEL_GLOBAL: any = null

const initialRoomSettings = {
  activity: activities.quizetro,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.hostInfo.map((text: string, index: number) => {
          return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
        })}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions
  }
}

const isDevMode = import.meta.env.MODE === 'development'

function searchUint8Array(array: number[], sequence: string[]): number[] {
  const decimalSequence = sequence.map((hex: any) => parseInt(hex, 16))
  const indices: number[] = []

  for (let i = 0; i < array.length - decimalSequence.length + 1; i++) {
    let match = true
    for (let j = 0; j < decimalSequence.length; j++) {
      if (array[i + j] !== decimalSequence[j]) {
        match = false
        break
      }
    }
    if (match) {
      indices.push(i)
    }
  }
  console.log('indices', indices)
  return indices // Return array of indices where the sequence is found
}

enum GameState {
  OFF,
  ATTRACT,
  SETUP,
  CAN_START,
  STARTING,
  BOARD,
  DRAWING,
  BUZZED_IN,
  FIRST_GUESS,
  SECOND_GUESS,
  POST_GUESS,
  RESULT,
  END
}

const Page: React.FC = () => {
  const [roomCode, setRoomCode] = useState('')
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const stageContainerRef = useRef<any>(null)

  // const [guesses, setGuesses] = useState<Guess[]>([])

  const emuRef = useRef<any | null>(null)
  const gameCanvasRef = useRef<any | null>(null)
  const moduleRef = useRef<any | null>(null)

  const gameTickInterval = useRef<any | null>(null)
  const guessTickInterval = useRef<any | null>(null)
  const gameSettingsRef = useRef<{ bots: number; difficulty: number } | null>(
    null
  )
  const phaseRef = useRef(GameState.OFF)
  const framePauseRef = useRef<number>(0)
  const [phase, setPhase] = useState(GameState.OFF)
  const buzzedInRef = useRef<number>(-1)
  // const [buzzedIn, setBuzzedIn] = useState(-1)
  const memoffsetRef = useRef<number>(0)

  const guessRef = useRef<string>('')
  const guessState = useRef<string>('drawing')
  const correctLettersRef = useRef<string>('')

  const playersRef = useRef<any[]>([])
  const [players, setPlayers] = useState<PlayerType[]>([])

  const memGet = (address: number): number => {
    return moduleRef.current.HEAPU8.at(memoffsetRef.current + address)
  }

  const ramDump = (): number => {
    return moduleRef.current.HEAPU8.slice(
      memoffsetRef.current,
      memoffsetRef.current + 0x800
    )
  }
  const memSet = (address: number, value: number): void => {
    const heap = moduleRef.current.HEAPU8
    heap[memoffsetRef.current + address] = value
    moduleRef.current.HEAPU8.set(heap)
  }

  const tickAttract = (): void => {
    if (playersRef.current.length > 0) {
      setPhase(GameState.SETUP)
    }
  }

  const tickSetup = (): void => {
    if (memGet(0x70) < 0x18) {
      emuRef.current.press({ button: 'start', player: 1 })
    } else {
      if (memGet(0x9e) === 1 && memGet(0x9f) === 2) {
        if (memGet(0xac) !== playersRef.current.length) {
          memSet(0xac, playersRef.current.length)
        }
        if (phaseRef.current === GameState.SETUP) {
          setPhase(GameState.CAN_START)
        }
        if (
          gameSettingsRef.current != null &&
          phaseRef.current === GameState.CAN_START
        ) {
          setPhase(GameState.STARTING)
        }
      }
    }
  }

  const tickStarting = (): void => {
    if (framePauseRef.current > 0) {
      framePauseRef.current--
      return
    }
    if (memGet(0x9e) === 1 && memGet(0x9f) === 2) {
      emuRef.current.press({ button: 'start', player: 1 })
    }
    if (memGet(0x9e) === 2 && memGet(0x9f) === 2) {
      const botNum = Math.min(
        (gameSettingsRef.current?.bots ?? 0) + 1,
        5 - playersRef.current.length
      )
      memSet(0xad, botNum)
      emuRef.current.press({ button: 'start', player: 1 })
      emuRef.current.press({ button: 'a', player: 1 })
      framePauseRef.current = 30
    }
    if (memGet(0x9e) === 3 && memGet(0x9f) === 2) {
      memSet(0xb0, gameSettingsRef.current?.difficulty ?? 0)
      memSet(0xaf, gameSettingsRef.current?.difficulty ?? 0)
      emuRef.current.press({ button: 'start', player: 1 })
      emuRef.current.press({ button: 'a', player: 1 })
      framePauseRef.current = 30
    }

    if (memGet(0x0) === 0x88) {
      setPhase(GameState.BOARD)
    }
  }

  const tickBoard = (): void => {
    if (memGet(0x0) === 0x82) {
      sendDrawing()
      setPhase(GameState.DRAWING)
    }
  }

  const tickDrawing = (): void => {
    if (memGet(0x46e) > 0) {
      buzzedInRef.current = memGet(0x46e) - 1
      sendBuzzedIn(playersRef.current[memGet(0x46e) - 1]?.clientId ?? 'BOT')
      setPhase(GameState.BUZZED_IN)
    }
  }

  const tickBuzzedIn = (): void => {
    if (memGet(0x46e) === 0) {
      setPhase(GameState.DRAWING)
      sendDrawing()
    }
    if (memGet(0x0) === 0x88) {
      setPhase(GameState.BOARD)
    }
  }

  const tickPostGuess = (): void => {
    if (memGet(0x46e) === 0) {
      sendDrawing()
      setPhase(GameState.DRAWING)
    }
  }

  useEffect(() => {
    switch (phase) {
      case GameState.OFF:
        break
      case GameState.ATTRACT:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickAttract, 1000 / 60)
        break
      case GameState.SETUP:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickSetup, 1000 / 60)
        break
      case GameState.CAN_START:
        sendPlayers()
        break
      case GameState.STARTING:
        clearInterval(gameTickInterval.current)
        sendPlayers()
        gameTickInterval.current = setInterval(tickStarting, 1000 / 60)
        break
      case GameState.BOARD:
        clearInterval(gameTickInterval.current)
        sendResetTries()
        gameTickInterval.current = setInterval(tickBoard, 1000 / 60)
        break
      case GameState.DRAWING:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickDrawing, 1000 / 60)
        break
      case GameState.BUZZED_IN:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickBuzzedIn, 1000 / 60)
        setPhase(GameState.FIRST_GUESS)
        break
      case GameState.FIRST_GUESS:
        break
      case GameState.SECOND_GUESS:
        break
      case GameState.POST_GUESS:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickPostGuess, 1000 / 60)
        break
      case GameState.RESULT:
        break
      case GameState.END:
        break
      default:
    }
    phaseRef.current = phase
  }, [phase])

  const testGame = (file: File): void => {
    const reader = new FileReader()

    reader.onload = function (event) {
      if (event.target != null) {
        const arrayBuffer = event.target.result as ArrayBuffer
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer)
        const hash = MD5(wordArray).toString()
        console.log('File hash:', hash)

        // Compare the hash with the expected hash
        const expectedHash = 'd30a1064cdd6ddcc4f47e0b5197dffb3'
        if (hash.toLowerCase() === expectedHash.toLowerCase()) {
          // Load the game if the hash matches
          console.log('Loading game...')

          loadGame(file).catch(console.error)
        } else {
          console.log('File hash does not match. Aborting...')
          setErrorMessage('Wrong Game')
          // loadGame(file).catch(console.error)
        }
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const loadGame = async (file: any): Promise<void> => {
    if (emuRef.current != null) {
      return
    }

    Nostalgist.launch({
      rom: file,
      core: 'quicknes',
      // core: 'fceumm',
      // state: blob,
      retroarchConfig: {
        input_player2_up: 'o',
        input_player2_left: 'k',
        input_player2_down: 'l',
        input_player2_right: ';',
        input_player2_select: '[',
        input_player2_start: ']',
        input_player2_a: 'i',
        input_player2_b: 'p'
      },
      element: gameCanvasRef.current,
      style: {
        width: '100%',
        height: '100%'
      }
    })
      .then((emu) => {
        emuRef.current = emu
        moduleRef.current = emu.getEmscriptenModule()
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(() => {
          let tester = []
          const testOffset = 0x30
          tester = []
          tester = searchUint8Array(moduleRef.current.HEAPU8, [
            '24',
            '27',
            '0f',
            '32',
            '22',
            '02'
          ])

          if (tester.length > 1) {
            console.log('found', tester)
            memoffsetRef.current = tester[0] - 0x30
            setPhase(GameState.ATTRACT)
            clearInterval(gameTickInterval.current)
          } else {
            console.log('almost')
          }
          if (tester.length === 0) {
            console.log('not found')
            setErrorMessage('Wrong Game')
            clearInterval(gameTickInterval.current)
          }
        }, 1000 / 60)
      })
      .catch(console.error)
  }

  const triggerAction = (message: any): void => {
    const playerIndex = playersRef.current.findIndex((player: any) => {
      return player.clientId === message.clientId
    })
    switch (message.action) {
      case 'start':
        gameSettingsRef.current = {
          bots: message.maxBots,
          difficulty: message.difficulty
        }
        break
      case 'buzz':
        pushBuzzer(playerIndex)
        break
      case 'guess':
        tryGuess(message.guess)
        // if (phaseRef.current === GameState.BUZZED_IN) {
        //   setPhase(GameState.FIRST_GUESS)
        // } else {
        //   setPhase(GameState.SECOND_GUESS)
        // }
        // capture guess and then type i letter by letter in game
        break
    }
  }

  const triggerEnter = (message: any): void => {
    if (message.self) return
    if (
      playersRef.current.some(
        (player: any) => player.clientId === message.alias
      )
    ) {
      // sendPlayers()
      return
    }

    if (playersRef.current.length >= 4) {
      CHANNEL_GLOBAL.send({
        type: 'client-message_full',
        id: CHANNEL_GLOBAL.myID,
        clientId: message.alias
      })
      return
    }

    playersRef.current.push({
      clientId: message.alias
    })
    setPlayers([...playersRef.current])
    // sendPlayers()
  }

  const QICallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
      CHANNEL_GLOBAL = results.channel
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }

  const sendPlayers = (): void => {
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'players',
      clientId: CHANNEL_GLOBAL.myAlias,
      players: playersRef.current
    })
  }

  const sendDrawing = (): void => {
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'phase',
      clientId: CHANNEL_GLOBAL.myAlias,
      phase: 'DRAWING'
    })
  }

  const sendResetTries = (): void => {
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'reset_tries',
      clientId: CHANNEL_GLOBAL.myAlias
    })
  }

  const sendBuzzedIn = (clientId: string): void => {
    console.log('sending buzzed in player')
    correctLettersRef.current = ''
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'buzzed_in',
      clientId: CHANNEL_GLOBAL.myAlias,
      buzzedIn: clientId
    })
  }

  const sendWrongGuess = (): void => {
    console.log('sending wrong guess')
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'wrong_guess',
      clientId: CHANNEL_GLOBAL.myAlias,
      buzzedIn: playersRef.current[buzzedInRef.current].clientId,
      correctLetters: correctLettersRef.current
    })
  }

  const sendShortGuess = (): void => {
    console.log('sending short guess')
    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'short_guess',
      clientId: CHANNEL_GLOBAL.myAlias,
      buzzedIn: playersRef.current[buzzedInRef.current].clientId,
      correctLetters: correctLettersRef.current
    })
  }

  const pushBuzzer = (playerIndex: number): void => {
    emuRef.current.press(
      buttonMappingByPlayers[playersRef.current.length][playerIndex + 1]
    )
  }

  const tryGuess = (guess: string): void => {
    guessRef.current = guess.replace(/[^a-zA-Z]/g, '')
    tryLetter()
  }

  const tryLetter = (): void => {
    if (guessRef.current.length > 0) {
      guessState.current = 'trying'
      const letter = guessRef.current.charAt(0)
      const letterAlphaIndex = letter.toLowerCase().charCodeAt(0) - 97 + 1
      memSet(0x81, letterAlphaIndex * 8)
      emuRef.current.press({ button: 'a', player: 1 })
      emuRef.current.press({ button: 'a', player: 2 })

      console.log('trying letter', letter)
      framePauseRef.current = 15
      clearInterval(guessTickInterval.current)
      guessTickInterval.current = setInterval(() => {
        if (framePauseRef.current > 0) {
          framePauseRef.current--
          return
        }

        if (guessState.current === 'finished') {
          if (memGet(0x8a) === 0) {
            guessState.current = 'waiting'
            clearInterval(guessTickInterval.current)
            sendShortGuess()
            return
          } else {
            clearInterval(guessTickInterval.current)
            return
          }
        }

        if (guessState.current === 'waiting') {
          clearInterval(guessTickInterval.current)
          return
        }

        if (guessState.current === 'wrong') {
          if (memGet(0xc3) === 0) {
            guessState.current = 'waiting'
            switch (phaseRef.current) {
              case GameState.FIRST_GUESS:
                guessRef.current = ''
                sendWrongGuess()
                setPhase(GameState.SECOND_GUESS)
                break
              case GameState.SECOND_GUESS:
                guessRef.current = ''
                correctLettersRef.current = ''
                guessState.current = 'failed'

                setPhase(GameState.POST_GUESS)
                clearInterval(guessTickInterval.current)

                break
              default:
            }
            clearInterval(guessTickInterval.current)
          } else if (memGet(0x8a) === 2) {
            setPhase(GameState.POST_GUESS)
            clearInterval(guessTickInterval.current)
          }
          return
        }

        if (guessState.current === 'failed') {
          if (memGet(0xc3) === 0) {
            guessState.current = 'drawing'
            correctLettersRef.current = ''
            guessRef.current = ''
            clearInterval(guessTickInterval.current)
          }
          return
        }

        if (memGet(0x5) === 0) {
          if (memGet(0xc3) === 0) {
            correctLettersRef.current += letter
            if (guessRef.current.length > 1) {
              guessRef.current = guessRef.current.slice(1)
              clearInterval(guessTickInterval.current)
              tryLetter()
            } else {
              guessState.current = 'finished'
              framePauseRef.current = 60
            }
          } else if (memGet(0xc3) > 0) {
            guessState.current = 'wrong'
          }
        }
      }, 1000 / 60)
    }
  }

  useEffect(() => {
    setRoomSettings(initialRoomSettings)
    // refreshStage()

    window.addEventListener('resize', () => {
      // refreshStage()
    })
  }, [])

  useEffect(() => {
    if (!isConnecting && roomSettings !== null) {
      isConnecting = true

      // if (isDevMode) {      }
      connection('qi', QICallback, { triggerAction, triggerEnter })
    }
  }, [roomSettings])

  return (
    <Box sx={styles.container}>
      <Box
        ref={stageContainerRef}
        sx={{
          h: '100%',
          w: '100dvw',
          border: '0px solid rgba(0,0,0,0.25)'
        }}
      >
        <Center>
          <Box
            pos="absolute"
            inset="0 auto 0 auto"
            height="100%"
            // bg="red"
            aspectRatio={4 / 3}
          >
            <Box ref={gameCanvasRef} id="gameCanvas" as="canvas" />
          </Box>

          <Center
            inset="0 auto 0 auto"
            pos="absolute"
            aspectRatio={4 / 3}
            display={phase === GameState.OFF ? 'flex' : 'none'}
          >
            <Box bg="white" p="20px">
              <Heading textAlign="center" mb="5px">
                Select the Anticipation Rom
                <br />
                File on your computer
              </Heading>
              <Heading textAlign="center" fontSize="15px" mb="20px">
                (MD5: d30a1064cdd6ddcc4f47e0b5197dffb3)
              </Heading>
              <Input
                type="file"
                onChange={(e) => {
                  if (e?.target?.files != null) {
                    testGame(e.target.files[0])
                  }
                }}
              />
            </Box>
          </Center>
        </Center>
        {/* <Box inset="auto 0 0 auto">
          <Button
            onClick={() => {
              // tryLocalGuess()
            }}
          >
            Try Letter
          </Button>

          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async () => {
              await loadGame('http://localhost:3000/states/anti.nes')
            }}
          >
            Load Game
          </Button>
        </Box> */}
        <Box
          pos="absolute"
          inset="40px auto 40px 0"
          display="none"
          bg="white"
          p="20px"
          // borderTopRightRadius="12px"
          // borderBottomRightRadius="12px"
          background="yellow.300"
          overflow="hidden"
          boxShadow="md"
        >
          <Heading>Score</Heading>
          <Grid
            w="20em"
            templateColumns="1fr 1fr"
            sx={{
              '& .th': {
                bg: 'gray.700',
                color: 'white',
                textAlign: 'center'
              },
              '& .tr': {
                textAlign: 'center'
              },
              '& .row-1': {
                bg: 'rgba(0,0,0,0.1)'
              }
            }}
          >
            <>
              <GridItem className="th">Name</GridItem>
              <GridItem className="th">Time</GridItem>
            </>
            {/* {guesses.map((guess, key) => {
              return (
                <>
                  <GridItem className={`tr row-${key % 2}`}>
                    {guess.clientId}
                  </GridItem>
                  <GridItem className={`tr row-${key % 2}`}>
                    {Math.round(guess.time)} Sec
                  </GridItem>
                </>
              )
            })} */}
          </Grid>
        </Box>
      </Box>
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => {
          setErrorMessage(null)
        }}
      >
        {errorMessage}
      </ErrorModal>
      <ScreenSizeErrorModal />

      <RoomCode
        roomCode={roomCode}
        hide={phase !== GameState.ATTRACT && phase !== GameState.SETUP}
      />
      <SideNav />
    </Box>
  )
}

const styles = {
  container: {
    backgroundColor: '#181425',
    pos: 'absolute',
    height: '100vdh',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  csv: {
    position: 'fixed',
    bottom: '0.25em',
    left: '0.25em',
    fontSize: '25px',
    zIndex: 1000
  }
}

export { Page }
